import { moiFetch, moiGenOrder } from "@/apis/reimburse";
import { fetch } from "@/apis/requirement";
import { numFormmater } from "@/pages/Requirement/util";
import { SConModal } from "@/styles/SConModal";
import STableLayout from "@/styles/STableLayout";
import SText from "@/styles/SText";
import { Product } from "@/types/product";
import { MoiReimbursRequest, OverIssueReasonTypeEnum } from "@/types/reimburse";
import { Requirement } from "@/types/requirement";
import { Button, Flex, Form, Input, Modal, notification, Spin, TableProps, Typography } from "antd";
import { AnyObject } from "antd/es/_util/type";
import { FC, Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useOverIssueReasonTypeEnum from "../hooks/useOverIssueReasonTypeEnum";
import errorCodes from "@/apis/error";
import { orderDescUpdate } from "@/apis/inventoryv2";
import { OrderV2 } from "@/types/inventoryV2";
import OrderDetailV2 from "@/pages/Warehouse/pages/Order/components/OrderDetailV2";

interface OverProductModalProps {
  open: boolean,
  moiReimburseId: string;
  onSucc: () => void;
  onClose: () => void;
};

interface ListDataType {
  product: Product;
  reasonType: OverIssueReasonTypeEnum;
  reason: string;
  orderAmount: number;
  overCount: number;
}

const OverProductModal: FC<OverProductModalProps> = ({ open, moiReimburseId, onSucc, onClose }) => {
  const { t } = useTranslation();
  const { getOverIssueReasonTypeEnum } = useOverIssueReasonTypeEnum();
  const [loading, setLoading] = useState<boolean>(false);
  const [requirement, setRequirement] = useState<Requirement | null>(null);
  const [reimburse, setReimburse] = useState<MoiReimbursRequest | null>(null);
  const [order, setOrder] = useState<OrderV2 | null>(null);
  const [desc, setDesc] = useState<string>('');

  const listData = useMemo(() => {
    if (!!reimburse && !!requirement) {
      let res: ListDataType[] = [];
      reimburse.items.forEach(item => {
        let reqItem = requirement.items.find(i => i.id === item.requirementItemId)!;
        res.push({
          product: reqItem.product!,
          reasonType: item.reasonType,
          reason: item.reason ?? '',
          orderAmount: item.orderAmount,
          overCount: item.overCount,
        });
      });
      return [...res];
    }
    return [];
  }, [requirement, reimburse]);

  useEffect(() => {
    if (moiReimburseId) getData();
  }, [moiReimburseId]);

  const getData = async () => {
    setLoading(true);
    try {
      const reimData = await moiFetch(moiReimburseId);
      let reqId = reimData.items[0].requirementId;
      const reqData = await fetch(reqId);

      setRequirement({ ...reqData });
      setReimburse({ ...reimData });
      setLoading(false);
    } catch (error) {
      notification.error({ message: t('counting.loadFailed') });
      setLoading(false);
    }
  };


  const handleOk = async () => {
    if (loading) return;
    setLoading(true);
    try {
      let order = await moiGenOrder(moiReimburseId);
      if (desc) {
        await orderDescUpdate(order.id, order.desc + desc)
      }
      notification.success({ message: t("requirement.createPreOutInventorySuccess"), description: order.number });
      setLoading(false);
      setOrder({ ...order, desc: order.desc + desc })
    } catch (error: any) {
      setLoading(false);
      if (error?.code === 73007 || error?.code === 73008) {
        if (Array.isArray(error?.extra)) {
          let errCodes = requirement?.items
            ?.filter(item => error.extra.includes(item.productId) || error.extra.includes(item.product?.code))
            ?.map(item => item.product?.code ?? '')
            ?.join(', ')
          notification.error({
            placement: 'top',
            message: t('apiError.error31'),
            description: errCodes,
          });
        } else {
          notification.error({
            placement: 'top',
            message: t('requirement.createPreOutInventoryFail'),
            description: t('warehouse.insufficientInventory')
          });
        }
      } else if (error === 72809) {
        // 其他错误
        notification.error({
          placement: 'top',
          message: t('requirement.createPreOutInventoryFail'),
          description: t('warehouse.needFix'),
        });
      } else if (error === 73010) {
        notification.error({
          placement: 'top',
          message: t('requirement.createPreOutInventoryFail'),
          description: t('warehouse.locFailed'),
        });
      } else if ([73024, 73028].includes(error.code)) {
        notification.error({
          message: t('warehouse.insufficientInventory'),
          description: error?.extra?.productCode
        })
      } else {
        // 其他错误
        notification.error({
          placement: 'top',
          message: t('requirement.createPreOutInventoryFail'),
          description: errorCodes[error] || error,
        });
      }
    }
  };

  const columns: TableProps<ListDataType>['columns'] = [
    {
      title: <SText text={t("product.code")} />,
      dataIndex: ["product", "code"],
      width: 100,
      sorter: (a, b) => {
        const valA = a?.product?.code ?? "";
        const valB = b?.product?.code ?? "";
        return valA.localeCompare(valB);
      },
      render: (_, item) => <SText text={item?.product?.code ?? ''} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("product.name")} />,
      dataIndex: "name",
      width: 100,
      render: (_, item) => <SText text={item?.product?.name ?? ''} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("order.brand")} />,
      dataIndex: "model",
      width: 120,
      render: (_, item) => <SText text={item?.product?.model ?? ''} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("product.uom")} />,
      dataIndex: "uom",
      width: 80,
      render: (_, item) => <SText text={item?.product?.uom ?? ''} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("reimburse.orderAmount")} />,
      dataIndex: "orderAmount",
      width: 80,
      render: (_, item) => <SText text={numFormmater(item.orderAmount || 0)} style={{ width: "100%" }} />,
    },
    {
      title: t("requirement.outCount"),
      dataIndex: "outCount",
      width: 120,
      render: (value) => numFormmater(Number(value || 0)),
    },
    {
      title: t("reimburse.overCount"),
      dataIndex: "overCount",
      width: 120,
      render: (value) => numFormmater(Number(value || 0)),
    },
    {
      title: t("reimburse.reasonType"),
      dataIndex: "reasonType",
      width: 160,
      render: (value: OverIssueReasonTypeEnum) => {
        let info = getOverIssueReasonTypeEnum(value);
        return (
          info && <SText tagMode color={info.color} text={info.label} />
        )
      },
    },
    {
      title: t("reimburse.reason"),
      dataIndex: "reason",
      width: 200,
      ellipsis: true,
      render: (value) => <SText text={value || "-"} style={{ display: "inline-block", width: 200 }} />,
    },
  ];

  const headerChildren = (
    <Flex vertical style={{ flex: 1 }}>
      <Form.Item label={t('order.remark')} layout="vertical" >
        <Input.TextArea
          value={desc}
          onChange={e => setDesc(e.target.value)}
          autoSize={{ minRows: 2, maxRows: 2 }}
          placeholder={t('reimburse.plRemark')}
          maxLength={1000}
          showCount
        />
      </Form.Item>
    </Flex>
  )

  return (
    <Modal title={t('requirement.createPreOutInventory')}
      open={open} width={1200} onCancel={onClose}
      footer={
        <Flex justify='flex-end' align='center' gap={6} >
          <Button onClick={onClose} >{t('common.cancel')}</Button>
          <SConModal disable={loading} title={t('common.tip')} content={t('requirement.createPreOutInventoryCon')} onOk={handleOk}>
            <Button type="primary" loading={loading} >{t('common.confirm')}</Button>
          </SConModal>
        </Flex>
      }
    >
      <Spin spinning={loading}>
        <Flex style={{ height: "70vh" }} vertical >
          <Typography.Title level={5} style={{ margin: 0 }}>{t('sale.requirement')}: {requirement?.number ?? ''}</Typography.Title>
          {!!!order ? (
            <STableLayout
              columns={columns as Array<AnyObject>}
              dataSource={listData}
              total={listData.length}
              pagination={false}
              extHeight={70}
              headerChildren={headerChildren}
              scrollX={1000}
            />
          ) : (
            <Fragment>
              <Typography.Title level={5} style={{ margin: 0 }}>{t('common.oo')}: {order?.number ?? ''}</Typography.Title>
              <OrderDetailV2 noModal data={order} handleCancel={() => { }} handleRm={() => { }} />
            </Fragment>
          )}
        </Flex>
      </Spin>
    </Modal>
  )
};

export default OverProductModal;